function def(input) {
    const defaults = {};
    if (input.remoteConfigOnly === undefined && input.requiresLocalInstallation === undefined) {
        // MCP and native_api definitions are local packages/processes by default.
        // Everything else (internal_service, agent_runtime, messaging_api) is
        // remote/config-only unless explicitly marked.
        if (input.kind === "mcp" || input.kind === "native_api") {
            defaults.requiresLocalInstallation = true;
        }
        else {
            defaults.remoteConfigOnly = true;
        }
    }
    return { ...defaults, ...input };
}
export const SYSTEM_INTEGRATION_REGISTRY = [
    // AI Runtime — system built-in infrastructure
    def({
        id: "qwen-4b-fast",
        displayName: "Qwen 4B Fast",
        description: "Local/remote fast inference profile routed through the canonical Qwen gateway.",
        ownership: "system_builtin",
        kind: "internal_service",
        category: "ai",
        managedBySmartShikka: true,
        sourceUrl: "https://github.com/QwenLM/Qwen2.5",
        requiresSecret: true,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    def({
        id: "qwen-9b-reasoning",
        displayName: "Qwen 9B Reasoning",
        description: "Local/remote reasoning inference profile routed through the canonical Qwen gateway.",
        ownership: "system_builtin",
        kind: "internal_service",
        category: "ai",
        managedBySmartShikka: true,
        sourceUrl: "https://github.com/QwenLM/Qwen2.5",
        requiresSecret: true,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    def({
        id: "deepseek-v4-flash",
        displayName: "DeepSeek V4 Flash",
        description: "Hosted DeepSeek flash inference profile.",
        ownership: "system_builtin",
        kind: "internal_service",
        category: "ai",
        managedBySmartShikka: true,
        sourceUrl: "https://platform.deepseek.com/",
        requiresSecret: true,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    def({
        id: "deepseek-v4-pro",
        displayName: "DeepSeek V4 Pro",
        description: "Hosted DeepSeek pro inference profile.",
        ownership: "system_builtin",
        kind: "internal_service",
        category: "ai",
        managedBySmartShikka: true,
        sourceUrl: "https://platform.deepseek.com/",
        requiresSecret: true,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    def({
        id: "openai-provider",
        displayName: "OpenAI",
        description: "Hosted OpenAI-compatible provider adapter.",
        ownership: "system_builtin",
        kind: "internal_service",
        category: "ai",
        managedBySmartShikka: false,
        sourceUrl: "https://platform.openai.com/",
        requiresSecret: true,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    def({
        id: "anthropic-provider",
        displayName: "Anthropic",
        description: "Hosted Anthropic provider adapter.",
        ownership: "system_builtin",
        kind: "internal_service",
        category: "ai",
        managedBySmartShikka: false,
        sourceUrl: "https://docs.anthropic.com/",
        requiresSecret: true,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    def({
        id: "gemini-provider",
        displayName: "Gemini",
        description: "Hosted Google Gemini provider adapter.",
        ownership: "system_builtin",
        kind: "internal_service",
        category: "ai",
        managedBySmartShikka: false,
        sourceUrl: "https://ai.google.dev/",
        requiresSecret: true,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    // Knowledge / Coding
    def({
        id: "context7",
        displayName: "Context7",
        description: "Built-in knowledge/coding MCP for library documentation lookup.",
        ownership: "system_builtin",
        kind: "mcp",
        category: "knowledge",
        managedBySmartShikka: true,
        sourceUrl: "https://github.com/upstash/context7",
        packageIdentity: { name: "@upstash/context7-mcp", versionRange: "^0.1.0", channel: "stable" },
        transport: "stdio",
        runtimeCommand: { command: "npx", args: ["-y", "@upstash/context7-mcp@^0.1.0"], env: {} },
        expectedCapabilities: ["docs", "resolve"],
        requiresSecret: false,
        defaultScope: "project",
        visibleToUser: false,
    }),
    // Browser
    def({
        id: "chrome-devtools-mcp",
        displayName: "Chrome DevTools MCP",
        description: "Built-in browser backend that controls Chrome via the Chrome DevTools Protocol through the official ChromeDevTools MCP server.",
        ownership: "system_builtin",
        kind: "mcp",
        category: "browser",
        managedBySmartShikka: true,
        sourceUrl: "https://github.com/ChromeDevTools/chrome-devtools-mcp",
        packageIdentity: { name: "chrome-devtools-mcp", versionRange: "^0.1.0", channel: "stable" },
        transport: "stdio",
        runtimeCommand: {
            command: "npx",
            args: ["-y", "chrome-devtools-mcp@^0.1.0"],
            env: { CHROME_DEVTOOLS_MCP_BROWSER_URL: "http://127.0.0.1:9222" },
        },
        expectedCapabilities: ["browser_navigate", "browser_evaluate", "browser_screenshot", "browser_get_dom"],
        requiresSecret: false,
        defaultScope: "project",
        visibleToUser: false,
    }),
    def({
        id: "smart-shikka-browser-sidecar",
        displayName: "Smart Shikka Browser sidecar",
        description: "Existing browser control sidecar used as fallback or where the Chrome DevTools MCP is unavailable.",
        ownership: "system_builtin",
        kind: "native_api",
        category: "browser",
        managedBySmartShikka: true,
        requiresSecret: false,
        defaultScope: "project",
        visibleToUser: false,
    }),
    // Desktop
    def({
        id: "open-computer-use",
        displayName: "Open Computer Use / Native desktop control",
        description: "Native desktop input and screen-capture backend.",
        ownership: "system_builtin",
        kind: "native_api",
        category: "desktop",
        managedBySmartShikka: true,
        requiresSecret: false,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    def({
        id: "native-devtools-mcp",
        displayName: "native-devtools-mcp fallback",
        description: "Optional local desktop DevTools MCP fallback adapter.",
        ownership: "system_builtin",
        kind: "mcp",
        category: "desktop",
        managedBySmartShikka: true,
        sourceUrl: "https://github.com/ChromeDevTools/chrome-devtools-mcp",
        requiresSecret: false,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    def({
        id: "smart-shikka-desktop-sidecar",
        displayName: "Smart Shikka Desktop sidecar",
        description: "Existing desktop sidecar for brokered control.",
        ownership: "system_builtin",
        kind: "native_api",
        category: "desktop",
        managedBySmartShikka: true,
        requiresSecret: false,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    // Voice
    def({
        id: "voice-stt",
        requiresLocalInstallation: true,
        displayName: "Voice STT",
        description: "Speech-to-text engine for Smart Shikka voice input.",
        ownership: "system_builtin",
        kind: "internal_service",
        category: "voice",
        managedBySmartShikka: true,
        requiresSecret: false,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    def({
        id: "voice-tts",
        requiresLocalInstallation: true,
        displayName: "Voice TTS",
        description: "Text-to-speech engine for Smart Shikka voice output.",
        ownership: "system_builtin",
        kind: "internal_service",
        category: "voice",
        managedBySmartShikka: true,
        requiresSecret: false,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    def({
        id: "voice-fallback",
        requiresLocalInstallation: true,
        displayName: "Voice fallback",
        description: "Offline/local fallback voice engine.",
        ownership: "system_builtin",
        kind: "internal_service",
        category: "voice",
        managedBySmartShikka: true,
        requiresSecret: false,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    // Agents
    def({
        id: "dify",
        displayName: "Dify",
        description: "External agent runtime integration.",
        ownership: "system_builtin",
        kind: "agent_runtime",
        category: "agents",
        managedBySmartShikka: false,
        sourceUrl: "https://github.com/langgenius/dify",
        requiresSecret: true,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    def({
        id: "goose",
        displayName: "Goose",
        description: "External agent runtime integration.",
        ownership: "system_builtin",
        kind: "agent_runtime",
        category: "agents",
        managedBySmartShikka: false,
        sourceUrl: "https://github.com/block/goose",
        requiresSecret: true,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    def({
        id: "hermes",
        displayName: "Hermes",
        description: "External agent runtime integration.",
        ownership: "system_builtin",
        kind: "agent_runtime",
        category: "agents",
        managedBySmartShikka: false,
        sourceUrl: "https://github.com/brokenloop/hermes",
        requiresSecret: true,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    // Automation
    def({
        id: "n8n-system",
        displayName: "n8n system runtime",
        description: "n8n workflow automation connector for owner-approved system workflows.",
        ownership: "system_builtin",
        kind: "agent_runtime",
        category: "automation",
        managedBySmartShikka: false,
        sourceUrl: "https://github.com/n8n-io/n8n",
        requiresSecret: true,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    // Messaging
    def({
        id: "telegram-system",
        displayName: "Telegram system connector",
        description: "System Telegram bot/connector for approved messaging.",
        ownership: "system_builtin",
        kind: "messaging_api",
        category: "messaging",
        managedBySmartShikka: false,
        sourceUrl: "https://core.telegram.org/bots",
        requiresSecret: true,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    def({
        id: "whatsapp-system",
        displayName: "WhatsApp system connector",
        description: "System WhatsApp connector for approved messaging.",
        ownership: "system_builtin",
        kind: "messaging_api",
        category: "messaging",
        managedBySmartShikka: false,
        requiresSecret: true,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    def({
        id: "email-system",
        displayName: "Email system connector",
        description: "System Email/SMTP connector for approved messaging.",
        ownership: "system_builtin",
        kind: "messaging_api",
        category: "messaging",
        managedBySmartShikka: false,
        sourceUrl: "https://en.wikipedia.org/wiki/Simple_Mail_Transfer_Protocol",
        requiresSecret: true,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    def({
        id: "sms-system",
        displayName: "SMS system connector",
        description: "System SMS connector for approved messaging.",
        ownership: "system_builtin",
        kind: "messaging_api",
        category: "messaging",
        managedBySmartShikka: false,
        sourceUrl: "https://en.wikipedia.org/wiki/SMS",
        requiresSecret: true,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    // Payment
    def({
        id: "aarpay",
        displayName: "AARPay",
        description: "AARPay payment and refund integration.",
        ownership: "system_builtin",
        kind: "internal_service",
        category: "payment",
        managedBySmartShikka: false,
        sourceUrl: "https://aarpay.com/",
        requiresSecret: true,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    // Document / OCR
    def({
        id: "pdf-server",
        displayName: "PDF server",
        description: "PDF generation and conversion service.",
        ownership: "system_builtin",
        kind: "internal_service",
        category: "document",
        managedBySmartShikka: false,
        sourceUrl: "https://github.com/Smart-Shikka/pdf-server",
        requiresSecret: true,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    def({
        id: "ocr",
        displayName: "OCR",
        description: "Optical character recognition service.",
        ownership: "system_builtin",
        kind: "internal_service",
        category: "document",
        managedBySmartShikka: false,
        sourceUrl: "https://github.com/Smart-Shikka/ocr-runtime",
        requiresSecret: true,
        defaultScope: "owner",
        visibleToUser: false,
    }),
    // User-installable connections (shown in user catalog, not Super Admin built-ins)
    def({
        id: "github-user",
        displayName: "GitHub",
        description: "User or project GitHub connection for code/repository access via the official GitHub MCP server container.",
        ownership: "user_installable",
        kind: "mcp",
        category: "infrastructure",
        managedBySmartShikka: false,
        sourceUrl: "https://github.com/github/github-mcp-server",
        packageIdentity: { name: "ghcr.io/github/github-mcp-server", versionRange: "1.9.0", channel: "stable" },
        transport: "stdio",
        runtimeCommand: {
            command: "docker",
            args: ["run", "--rm", "-i", "-e", "GITHUB_PERSONAL_ACCESS_TOKEN", "ghcr.io/github/github-mcp-server:v1.9.0@sha256:881b53d6f75f69bdbc1b5b10fc2f1361717c19054143b3a8529fb5c32061a50e", "stdio", "--read-only"],
            env: { GITHUB_PERSONAL_ACCESS_TOKEN: "${secret:GITHUB_SHIKKA_TEST_PAT}" },
        },
        expectedCapabilities: ["get_file", "get_issue", "search_issues", "list_commits", "list_pull_requests"],
        requiresSecret: true,
        defaultScope: "project",
        visibleToUser: true,
    }),
    def({
        id: "supabase-user",
        displayName: "Supabase",
        description: "User or project Supabase connection using the canonical hosted MCP endpoint (https://mcp.supabase.com/mcp) scoped to a dev/test project with read-only mode by default. Falls back to a pinned local STDIO package only when explicitly configured.",
        ownership: "user_installable",
        kind: "mcp",
        category: "infrastructure",
        managedBySmartShikka: false,
        sourceUrl: "https://mcp.supabase.com/mcp",
        packageIdentity: { name: "@supabase/mcp-server-supabase", versionRange: "0.1.0", channel: "stable" },
        transport: "http",
        runtimeCommand: {
            command: "npx",
            args: ["-y", "@supabase/mcp-server-supabase@0.1.0"],
            env: {
                BASE_URL: "https://mcp.supabase.com",
                SUPABASE_MCP_DEV_CREDENTIALS: "${secret:SUPABASE_MCP_DEV_CREDENTIALS}",
                SUPABASE_MCP_TOKEN: "${secret:SUPABASE_MCP_TOKEN}",
            },
        },
        expectedCapabilities: ["query", "list_tables", "get_table_schema"],
        requiresSecret: true,
        defaultScope: "project",
        visibleToUser: true,
    }),
    def({
        id: "gmail-user",
        remoteConfigOnly: true,
        displayName: "Gmail",
        description: "User Gmail connection for read/send where product permits.",
        ownership: "user_installable",
        kind: "mcp",
        category: "messaging",
        managedBySmartShikka: false,
        sourceUrl: "https://developers.google.com/gmail",
        requiresSecret: true,
        defaultScope: "user",
        visibleToUser: true,
    }),
    def({
        id: "microsoft-user",
        remoteConfigOnly: true,
        displayName: "Microsoft",
        description: "User Microsoft connection for Outlook/OneDrive where product permits.",
        ownership: "user_installable",
        kind: "mcp",
        category: "infrastructure",
        managedBySmartShikka: false,
        sourceUrl: "https://learn.microsoft.com/en-us/graph/",
        requiresSecret: true,
        defaultScope: "user",
        visibleToUser: true,
    }),
    def({
        id: "apify-user",
        remoteConfigOnly: true,
        displayName: "Apify",
        description: "User Apify connection for web scraping/actors.",
        ownership: "user_installable",
        kind: "mcp",
        category: "infrastructure",
        managedBySmartShikka: false,
        sourceUrl: "https://github.com/apify/actors-mcp-server",
        requiresSecret: true,
        defaultScope: "project",
        visibleToUser: true,
    }),
    def({
        id: "custom-mcp-user",
        displayName: "Custom MCP",
        description: "Owner-approved custom MCP server.",
        ownership: "user_installable",
        kind: "mcp",
        category: "infrastructure",
        managedBySmartShikka: false,
        requiresSecret: true,
        defaultScope: "project",
        visibleToUser: true,
    }),
];
export function getSystemIntegrationRegistry() {
    return SYSTEM_INTEGRATION_REGISTRY;
}
export function getSystemIntegrationDefinitionById(id) {
    return SYSTEM_INTEGRATION_REGISTRY.find((d) => d.id === id);
}
export function getSystemBuiltinIntegrations() {
    return SYSTEM_INTEGRATION_REGISTRY.filter((d) => d.ownership === "system_builtin");
}
export function getUserInstallableIntegrations() {
    return SYSTEM_INTEGRATION_REGISTRY.filter((d) => d.ownership === "user_installable");
}
//# sourceMappingURL=systemIntegrationRegistry.js.map